/**
 * Formats uniques du produit (LOT-H1 — fondations du design system).
 *
 * L'audit visuel du 2026-08 a relevé **4 formats de date** (`03 août`,
 * `27/07/2026 14:33`, `15/02/2026`, `16 octobre 2025`) et **3 formats de devise
 * pour 2 devises** (`0.0400€`, `0.07 EUR`, `$18.06`) répartis sur 33 écrans, chaque
 * page ayant réécrit son propre `formatDate` / `formatDuration` local.
 *
 * Ce module est la source unique. Toute date, heure, durée ou montant affiché à
 * l'écran passe désormais par l'une de ces cinq fonctions. Écrire un
 * `toLocaleDateString` / `toFixed` de plus dans une page est une régression.
 *
 * ## Contrat (référence : français)
 *
 * | Fonction         | Rendu fr           | Exemple                |
 * |------------------|--------------------|------------------------|
 * | `formatDate`     | jour mois année    | `3 août 2026`          |
 * | `formatDateTime` | date, heure        | `3 août 2026, 15:16`   |
 * | `formatTime`     | heure:minute       | `15:16`                |
 * | `formatDuration` | m:ss (h:mm:ss)     | `2:41`, `1:02:41`      |
 * | `formatCurrency` | montant + devise   | `0,04 €`, `18,06 $US`  |
 *
 * ## Deux règles qui ne se négocient pas
 *
 * 1. **La locale suit i18next.** Le français est la référence des exemples
 *    ci-dessus, pas un format codé en dur : en anglais, `formatDate` rend
 *    `August 3, 2026`. La langue est lue **à chaque appel** sur `i18n.language`,
 *    jamais capturée au chargement du module — sinon un `changeLanguage()` ne
 *    rafraîchirait rien.
 * 2. **La devise est CELLE DE LA DONNÉE.** `formatCurrency` ne convertit jamais.
 *    Un coût Twilio en dollars s'affiche `18,06 $US` dans une UI française ; il ne
 *    devient pas des euros. Le rôle de cette fonction est d'unifier la *forme*
 *    (séparateur décimal, position du symbole), pas de réécrire la valeur.
 */
// ⚠️ `i18next` directement, PAS `../i18n`.
//
// C'est le MÊME singleton — `src/i18n/index.ts` se contente de configurer cet
// objet-là — mais l'importer par son module de configuration ferait entrer les
// 18 fichiers JSON de traduction et `react-i18next` dans le graphe de CHAQUE
// module qui formate une date. Les tests de composants qui mockent
// `react-i18next` sans exposer `initReactI18next` tombaient alors à la
// compilation, pour un utilitaire qui n'a besoin que du code de langue.
import i18next from 'i18next'

/**
 * Marqueur unique d'absence de valeur. Un seul caractère pour tout le produit, au
 * lieu du mélange actuel de `'-'`, `''` et `'N/A'`.
 */
export const EMPTY_VALUE = '—'

/** Tout ce qu'une API du backend peut renvoyer pour un instant. */
export type DateInput = Date | string | number | null | undefined

/** Options communes aux formateurs temporels. */
export interface TemporalFormatOptions {
  /**
   * Fuseau IANA (`'Europe/Paris'`). Par défaut : celui du navigateur.
   * Utile pour les rappels planifiés, affichés dans le fuseau **de l'utilisateur**
   * et non dans celui de la machine.
   */
  timeZone?: string
}

/**
 * Locale active, relue à chaque appel.
 *
 * `resolvedLanguage` est la langue réellement servie par i18next après repli ;
 * `language` peut valoir `'fr-CA'` alors que seule `'fr'` est chargée. On préfère
 * donc la première, et on retombe sur le français, qui est à la fois la langue par
 * défaut et la langue de repli de l'application.
 */
function currentLocale(): string {
  return i18next.resolvedLanguage || i18next.language || 'fr'
}

/**
 * Normalise une entrée en `Date` valide, ou `null`.
 *
 * `null`, `undefined`, chaîne vide et date invalide (`new Date('n'importe quoi')`)
 * retournent tous `null` : les appelants n'ont pas à se protéger eux-mêmes, c'est
 * exactement ce que faisaient les 30 gardes `if (!date) return '-'` dispersées.
 */
function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Date seule — `3 août 2026`.
 *
 * Le jour est en `numeric` et non `2-digit` : `3 août` et non `03 août`, la forme
 * naturelle en français.
 */
export function formatDate(value: DateInput, options: TemporalFormatOptions = {}): string {
  const date = toDate(value)
  if (!date) return EMPTY_VALUE
  return new Intl.DateTimeFormat(currentLocale(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: options.timeZone,
  }).format(date)
}

/**
 * Date COURTE — `18 déc. 2026` (ÉCRANS-RÉGLAGES).
 *
 * Pour un résumé d'UNE ligne, là où la largeur est comptée (rangée d'un rail
 * de sections : « Active · fin 18 déc. 2026 »). Même jour sans zéro que
 * `formatDate`, mois abrégé par `Intl` dans la langue active ; l'année reste
 * (une fin de campagne peut tomber l'année suivante). Partout ailleurs :
 * `formatDate`.
 */
export function formatShortDate(value: DateInput, options: TemporalFormatOptions = {}): string {
  const date = toDate(value)
  if (!date) return EMPTY_VALUE
  return new Intl.DateTimeFormat(currentLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: options.timeZone,
  }).format(date)
}

/**
 * Date et heure — `3 août 2026, 15:16`.
 *
 * Composé volontairement à partir de `formatDate` + `formatTime` plutôt que confié
 * à un seul `Intl.DateTimeFormat` : en français celui-ci rendrait `3 août 2026 à
 * 15:16`, et le séparateur arbitré est la virgule. La composition garantit aussi
 * que la partie date d'un `formatDateTime` est, au caractère près, ce que rend
 * `formatDate` sur la même valeur.
 */
export function formatDateTime(value: DateInput, options: TemporalFormatOptions = {}): string {
  const date = toDate(value)
  if (!date) return EMPTY_VALUE
  return `${formatDate(date, options)}, ${formatTime(date, options)}`
}

/**
 * Heure seule — `15:16`.
 *
 * `hour: '2-digit'` maintient l'alignement en colonne (`09:05` et non `9:05`).
 */
export function formatTime(value: DateInput, options: TemporalFormatOptions = {}): string {
  const date = toDate(value)
  if (!date) return EMPTY_VALUE
  return new Intl.DateTimeFormat(currentLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: options.timeZone,
  }).format(date)
}

/**
 * Durée en secondes — `2:41`, et `1:02:41` au-delà d'une heure.
 *
 * Format **non localisé, et c'est voulu** : une durée d'appel est un chronomètre,
 * pas une phrase. Elle se lit et se compare en colonne dans un tableau, ce qui
 * exclut les variantes verbales (`2 min 41 s`) que certaines pages utilisaient.
 *
 * ⚠️ Le rendu tabulaire promis par la charte est **typographique** : la classe
 * `tabular-nums` doit être portée par l'élément qui affiche le résultat, sinon les
 * chiffres à chasse variable font trembler la colonne à chaque tic d'horloge.
 *
 * Les secondes sont tronquées (`Math.floor`) et non arrondies : un appel de 2,9 s
 * affiche `0:02`, jamais `0:03`, pour ne pas dépasser la durée facturée.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return EMPTY_VALUE
  if (!Number.isFinite(seconds) || seconds < 0) return EMPTY_VALUE

  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60

  const pad = (n: number) => n.toString().padStart(2, '0')
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(secs)}`
    : `${minutes}:${pad(secs)}`
}

/** Options de `formatTimeAgo`. */
export interface TimeAgoOptions {
  /** Instant de référence (défaut : maintenant) — fixé par les tests et par l'horloge d'une liste. */
  now?: Date | number
  /** `short` : `il y a 5 min` (colonne de tableau) ; `long` : `il y a 5 minutes` (fiche). */
  style?: 'short' | 'long'
}

/** Paliers de `formatTimeAgo`, du plus grand au plus petit (secondes). */
const TIME_AGO_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 86400],
  ['month', 30 * 86400],
  ['week', 7 * 86400],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
]

/**
 * Temps écoulé depuis un instant, en phrase relative — `il y a 5 min`,
 * `il y a 3 h`, `il y a 2 j` (ÉQUIPE-2, #361 : « Vu il y a … »).
 *
 * `Intl.RelativeTimeFormat` dans la locale i18next relue à chaque appel (règle 1
 * du module) : `5 min ago` en anglais. Le palier est TRONQUÉ (`Math.floor`) :
 * 119 minutes font `il y a 1 h`, jamais `il y a 2 h` — on ne vieillit pas un
 * signe de vie. Un instant futur (horloges décalées) se lit `il y a 0 s`.
 */
export function formatTimeAgo(value: DateInput, options: TimeAgoOptions = {}): string {
  const date = toDate(value)
  if (!date) return EMPTY_VALUE
  const now = options.now instanceof Date ? options.now.getTime() : (options.now ?? Date.now())
  const seconds = Math.max(0, Math.floor((now - date.getTime()) / 1000))
  const rtf = new Intl.RelativeTimeFormat(currentLocale(), { numeric: 'always', style: options.style ?? 'short' })
  for (const [unit, size] of TIME_AGO_UNITS) {
    if (seconds >= size) return rtf.format(-Math.floor(seconds / size), unit)
  }
  return rtf.format(-seconds, 'second')
}

/**
 * #353 — TEMPS CUMULÉ destiné à être lu : « 407 h 12 » (`407h 12m` en
 * anglais) au-delà d'une heure, `formatDuration` en dessous (« 4:05 »).
 *
 * La planche 2 (et l'audit C-10) jugent « 407:11:45 » illisible pour un
 * temps de parole cumulé : les heures se lisent, les secondes n'apportent
 * rien. Minutes TRONQUÉES, comme les secondes de `formatDuration`.
 */
export function formatHoursMinutes(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return EMPTY_VALUE
  const total = Math.floor(seconds)
  if (total < 3600) return formatDuration(total)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const hh = new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 0 }).format(hours)
  const mm = minutes.toString().padStart(2, '0')
  return currentLocale().startsWith('fr') ? `${hh}\u00a0h\u00a0${mm}` : `${hh}h ${mm}m`
}

/**
 * Nombre ENTIER destiné à être lu — `35 479`, `1 204` (LOT-H10, généralisé par
 * LOT-RT-DA2 / DA-051).
 *
 * La recette DA passe 2 a relevé, sur `/supervision`, un compteur **`15327`**
 * sans séparateur de milliers là où `/contacts` écrit correctement `35 479` :
 * le groupement n'était appliqué qu'aux compteurs passés par `formatCount`.
 * `formatNumber` est le formateur générique ; tout entier AFFICHÉ passe par
 * lui (ou par `formatCount`, son alias sémantique pour les cardinaux).
 *
 * Comme les autres formats, la locale suit i18next et est relue à chaque
 * appel : `fr` groupe par espace fine insécable, `en` par virgule. **Ne jamais
 * coder ce séparateur en dur** — ni dans le produit, ni dans un test.
 *
 * Une durée passe par `formatDuration`, un montant par `formatCurrency`, un
 * pourcentage par `formatPercent`.
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY_VALUE
  return new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 0 }).format(value)
}

/**
 * Cardinal d'un ensemble (« 35 479 contacts ») — alias sémantique de
 * `formatNumber`, conservé pour les compteurs honnêtes de H10 (DA-003) : un
 * compteur est un COMPTE, et le nom au point d'appel le dit.
 */
export function formatCount(value: number | null | undefined): string {
  return formatNumber(value)
}

/** Options de `formatPercent`. */
export interface PercentFormatOptions {
  /**
   * Nombre MAXIMAL de décimales (défaut : 1). Le minimum reste 0 : un
   * pourcentage entier s'écrit `100 %`, jamais `100,0 %` — c'est la
   * coexistence de `100.0%` et `100%` sur le même écran que DA-051 relève.
   */
  decimals?: number
}

/**
 * Pourcentage destiné à être lu — `52,4 %`, `100 %` (`52.4%`, `100%` en
 * anglais) (LOT-RT-DA2 / DA-051).
 *
 * `value` est le taux tel que le backend le livre, sur l'échelle **0–100**
 * (`successRate: 82.4`), pas une fraction 0–1.
 *
 * Le rendu passe par `Intl` en style `percent` : c'est lui qui détient la
 * place du symbole et l'espace qui le précède — insécable en français, absente
 * en anglais. **Ne jamais concaténer `%` à la main** : c'est précisément la
 * concaténation au point d'appel qui a produit les trois écritures relevées
 * par la recette (`100.0%`, `100%`, `0.0%`).
 */
export function formatPercent(
  value: number | null | undefined,
  options: PercentFormatOptions = {},
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY_VALUE
  return new Intl.NumberFormat(currentLocale(), {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: options.decimals ?? 1,
  }).format(value / 100)
}

/** Options de `formatCurrency`. */
export interface CurrencyFormatOptions {
  /**
   * Nombre maximal de décimales.
   *
   * **Réservé aux tarifs unitaires**, où la précision est l'information : un prix
   * au SMS vaut `0,0400 €` et l'arrondir à `0,04 €` détruit la donnée affichée sur
   * un écran de configuration. Un MONTANT (facture, solde, coût cumulé) ne passe
   * jamais cette option : il garde les 2 décimales de sa devise.
   */
  maximumFractionDigits?: number
}

/**
 * Montant dans **sa** devise — `0,04 €`, `18,06 $US`.
 *
 * `currency` est un code ISO 4217 (`'EUR'`, `'USD'`). Il vient de la donnée, jamais
 * d'une préférence d'affichage : aucune conversion n'est faite ni ne doit l'être.
 *
 * Un code inconnu ou vide ne fait pas tomber la page — `Intl.NumberFormat` lève un
 * `RangeError` sur un code non conforme, on se rabat alors sur `montant + code`.
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  options: CurrencyFormatOptions = {},
): string {
  // MESSAGERIE-UX (#502 🟠1) : un `Decimal` Prisma (`Sms.cost`,
  // `SmsProvider.costPerSms`) arrive sérialisé en CHAÎNE (`"0.0800"`) — le
  // coût SMS était « — » partout (journal, volet, carte fournisseur). Une
  // chaîne numérique vaut son nombre ; une chaîne vide ou non numérique vaut
  // l'absence, jamais `0`.
  const value = typeof amount === 'string' ? (amount.trim() === '' ? Number.NaN : Number(amount)) : amount
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY_VALUE
  }

  const locale = currentLocale()
  const code = (currency ?? '').trim().toUpperCase()

  if (code) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        maximumFractionDigits: options.maximumFractionDigits,
        // Sans cette ligne, une `maximumFractionDigits` de 4 laisserait le minimum à
        // 2 et rendrait `0,04 €` pour 0.0400 : la précision demandée serait ignorée.
        minimumFractionDigits: options.maximumFractionDigits,
      }).format(value)
    } catch {
      // Code non ISO 4217 — on continue vers le repli ci-dessous.
    }
  }

  const number = new Intl.NumberFormat(locale, {
    minimumFractionDigits: options.maximumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(value)
  return code ? `${number} ${code}` : number
}
